import React from "react";
import { useState, useEffect } from "react";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import Swal from "sweetalert2";
import Dialog from "@mui/material/Dialog";
import { Slide } from "@mui/material";
import Grid from "@mui/material/Grid";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});
Transition.displayName = "Transition";

// Enhanced DetailItem component with better styling
const DetailItem = ({ label, value, icon }) => (
  <MDBox
    mb={2}
    p={2}
    sx={{
      backgroundColor: "rgba(0,0,0,0.02)",
      borderRadius: "8px",
      border: "1px solid rgba(0,0,0,0.1)",
    }}
  >
    <MDBox display="flex" alignItems="center" mb={0.5}>
      {icon && <Icon sx={{ mr: 1, color: "info.main" }}>{icon}</Icon>}
      <MDTypography variant="caption" fontWeight="bold" color="info">
        {label}
      </MDTypography>
    </MDBox>
    <MDTypography variant="body2" sx={{ pl: icon ? 4 : 0 }}>
      {value || "Not provided"}
    </MDTypography>
  </MDBox>
);

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  icon: PropTypes.string,
};

// Enhanced Member Details Modal Component
const MemberDetailsModal = ({ member, open, onClose }) => {
  if (!member) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={true}
      maxWidth="lg"
      TransitionComponent={Transition}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
        },
      }}
    >
      <MDBox p={4}>
        {/* Header Section */}
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          pb={2}
          sx={{ borderBottom: "1px solid rgba(0,0,0,0.1)" }}
        >
          <MDBox display="flex" alignItems="center">
            <Icon sx={{ mr: 2, fontSize: "2rem", color: "info.main" }}>person</Icon>
            <MDTypography variant="h4" color="info">
              My Member Profile
            </MDTypography>
          </MDBox>
          <MDButton
            variant="text"
            color="secondary"
            onClick={onClose}
            sx={{ minWidth: "auto", p: 1 }}
          >
            <Icon>close</Icon>
          </MDButton>
        </MDBox>

        {/* Member Header with Photo */}
        <MDBox
          display="flex"
          alignItems="center"
          mb={4}
          p={3}
          sx={{
            backgroundColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "12px",
            background:
              "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
          }}
        >
          {member.photo ? (
            <MDBox
              component="img"
              src={`https://api.ofi-cia.com/storage/${member.photo}`}
              alt="Member"
              sx={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                objectFit: "cover",
                mr: 3,
                border: "4px solid white",
                boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
              }}
            />
          ) : (
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                backgroundColor: "info.main",
                mr: 3,
                color: "white",
              }}
            >
              <Icon sx={{ fontSize: "3rem" }}>person</Icon>
            </MDBox>
          )}
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold" mb={1}>
              {member.firstName} {member.middleName} {member.surname}
            </MDTypography>
            <MDBox display="flex" alignItems="center" mb={1}>
              <Icon sx={{ mr: 1, color: "info.main" }}>badge</Icon>
              <MDTypography variant="h6" color="info">
                ID: {member.memberId || "Not assigned"}
              </MDTypography>
            </MDBox>
            <MDBox display="flex" alignItems="center">
              <Icon
                sx={{ mr: 1, color: member.status === "active" ? "success.main" : "error.main" }}
              >
                {member.status === "active" ? "check_circle" : "cancel"}
              </Icon>
              <MDTypography
                variant="button"
                color={member.status === "active" ? "success" : "error"}
                fontWeight="bold"
              >
                {member.status?.toUpperCase()}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Content Sections */}
        <MDBox sx={{ maxHeight: "60vh", overflow: "auto", pr: 1 }}>
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12} md={6}>
              <MDBox
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(102, 126, 234, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(102, 126, 234, 0.2)",
                }}
              >
                <MDTypography variant="h5" fontWeight="bold" mb={3} color="info">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>person_outline</Icon>
                  Personal Information
                </MDTypography>
                <DetailItem label="First Name" value={member.firstName} icon="person" />
                <DetailItem label="Middle Name" value={member.middleName} icon="person" />
                <DetailItem label="Surname" value={member.surname} icon="person" />
                <DetailItem label="Gender" value={member.gender} icon="wc" />
                <DetailItem label="Date of Birth" value={member.dateOfBirth} icon="cake" />
                <DetailItem label="Marital Status" value={member.maritalStatus} icon="favorite" />
                <DetailItem label="Phone Number" value={member.phoneNumber} icon="phone" />
                <DetailItem label="WhatsApp Number" value={member.whatsappNumber} icon="chat" />
                <DetailItem label="Email" value={member.email} icon="email" />
              </MDBox>
            </Grid>

            {/* Address Information */}
            <Grid item xs={12} md={6}>
              <MDBox
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(76, 175, 80, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(76, 175, 80, 0.2)",
                }}
              >
                <MDTypography variant="h5" fontWeight="bold" mb={3} color="success">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>location_on</Icon>
                  Address Information
                </MDTypography>
                <DetailItem
                  label="Residential Address"
                  value={member.residentialAddress}
                  icon="home"
                />
                <DetailItem label="Postal Address" value={member.postalAddress} icon="mail" />
                <DetailItem label="Region" value={member.region} icon="public" />
                <DetailItem label="Home Town" value={member.homeTown} icon="location_city" />
                <DetailItem label="Nationality" value={member.nationality} icon="flag" />
                <DetailItem label="Place of Birth" value={member.placeOfBirth} icon="place" />
              </MDBox>
            </Grid>

            {/* Professional Information */}
            <Grid item xs={12} md={6}>
              <MDBox
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(255, 152, 0, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(255, 152, 0, 0.2)",
                }}
              >
                <MDTypography variant="h5" fontWeight="bold" mb={3} color="warning">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>work</Icon>
                  Professional Information
                </MDTypography>
                <DetailItem label="Occupation" value={member.occupation} icon="business_center" />
                <DetailItem label="Profession" value={member.profession} icon="work_outline" />
                <DetailItem label="Rank/Position" value={member.rank} icon="military_tech" />
                <DetailItem label="Employment" value={member.employment} icon="work" />
                <DetailItem label="Employer" value={member.nameOfEmployer} icon="domain" />
                <DetailItem label="Education" value={member.education} icon="school" />
              </MDBox>
            </Grid>

            {/* Family Information */}
            <Grid item xs={12} md={6}>
              <MDBox
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(233, 30, 99, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(233, 30, 99, 0.2)",
                }}
              >
                <MDTypography variant="h5" fontWeight="bold" mb={3} color="error">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>family_restroom</Icon>
                  Family Information
                </MDTypography>
                <DetailItem label="Spouse Name" value={member.spouseName} icon="favorite" />
                <DetailItem label="Spouse Phone" value={member.spousePhoneNumber} icon="phone" />
                <DetailItem
                  label="Date of Marriage"
                  value={member.dateOfMarriage}
                  icon="favorite_border"
                />
                <DetailItem
                  label="Number of Children"
                  value={member.numberOfChildren}
                  icon="child_care"
                />
                <DetailItem label="Male Children" value={member.numberOfMales} icon="boy" />
                <DetailItem label="Female Children" value={member.numberOfFemales} icon="girl" />
                <DetailItem label="Parents" value={member.nameOfParents} icon="elderly" />
                <DetailItem label="Next of Kin" value={member.nextOfKin} icon="contact_emergency" />
              </MDBox>
            </Grid>

            {/* Church Information */}
            <Grid item xs={12} md={6}>
              <MDBox
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(156, 39, 176, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(156, 39, 176, 0.2)",
                }}
              >
                <MDTypography variant="h5" fontWeight="bold" mb={3} color="secondary">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>church</Icon>
                  Church Information
                </MDTypography>
                <DetailItem label="Department" value={member.department} icon="group" />
                <DetailItem label="Ministry" value={member.ministry} icon="volunteer_activism" />
                <DetailItem label="Status" value={member.status} icon="verified" />
                <DetailItem
                  label="Year of Conversion"
                  value={member.yearOfConversion}
                  icon="auto_awesome"
                />
                <DetailItem label="Baptized" value={member.areYouBaptized} icon="water_drop" />
                <DetailItem label="Baptism Type" value={member.baptismType} icon="waves" />
                <DetailItem label="Baptism Date" value={member.baptismDate} icon="event" />
                <DetailItem label="Baptism Church" value={member.baptismChurch} icon="church" />
                <DetailItem label="Baptized By" value={member.baptismBy} icon="person" />
              </MDBox>
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12} md={6}>
              <MDBox
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "rgba(63, 81, 181, 0.05)",
                  borderRadius: "12px",
                  border: "1px solid rgba(63, 81, 181, 0.2)",
                }}
              >
                <MDTypography variant="h5" fontWeight="bold" mb={3} color="primary">
                  <Icon sx={{ mr: 1, verticalAlign: "middle" }}>info</Icon>
                  Additional Information
                </MDTypography>
                <DetailItem
                  label="Previous Church"
                  value={member.nameOfPreviousChurch}
                  icon="church"
                />
                <DetailItem
                  label="Place of Interest"
                  value={member.placeOfInterest}
                  icon="interests"
                />
                <DetailItem label="Will Pay Tithe" value={member.willYouPayTithe} icon="payment" />
                <DetailItem
                  label="Communicant"
                  value={member.communicant}
                  icon="record_voice_over"
                />
                <DetailItem
                  label="Will Pay Welfare"
                  value={member.willYouPayWelfare}
                  icon="volunteer_activism"
                />
                <DetailItem
                  label="Membership Date"
                  value={member.membershipDate}
                  icon="event_available"
                />
                <DetailItem label="Role" value={member.role} icon="admin_panel_settings" />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* Footer Actions */}
        <MDBox
          display="flex"
          justifyContent="flex-end"
          mt={3}
          pt={2}
          sx={{ borderTop: "1px solid rgba(0,0,0,0.1)" }}
        >
          <MDButton variant="gradient" color="info" onClick={onClose}>
            <Icon sx={{ mr: 1 }}>close</Icon>
            Close
          </MDButton>
        </MDBox>
      </MDBox>
    </Dialog>
  );
};

MemberDetailsModal.propTypes = {
  member: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default function useMemberData() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showModalOpen, setShowModalOpen] = useState(false);

  const fetchMemberData = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("authToken");
      const userId = localStorage.getItem("user");

      // Parse the user data from JSON string
      const user = JSON.parse(userId);
      const memberId = user.member_id;
      //console.log("Member ID:", memberId);

      if (!memberId) {
        throw new Error("User ID not found. Please log in again.");
      }

      // Fetch only the logged-in user's member data
      const response = await fetch(`https://api.ofi-cia.com/api/members/user/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      const member = result.data || result.member || result[0];
      console.log(member);

      if (member) {
        const memberData = {
          id: member.id,
          memberId: member.memberID || "N/A",
          firstName: member.first_name || "N/A",
          surname: member.surname || "N/A",
          middleName: member.middle_name || "",
          gender: member.gender || "N/A",
          department: member.department?.name || "No Department",
          ministry: member.ministry?.name || "",
          status: member.status ? "active" : "inactive",
          dateOfBirth: member.date_of_birth || "",
          phoneNumber: member.phone || "",
          email: member.email || "",
          residentialAddress: member.residential_address || "",
          postalAddress: member.postal_address || "",
          region: member.region || "",
          homeTown: member.home_town || "",
          nationality: member.nationality || "",
          placeOfBirth: member.place_of_birth || "",
          maritalStatus: member.marital_status || "",
          profession: member.profession || "",
          rank: member.rank || "",
          employment: member.employment || "",
          nameOfEmployer: member.name_of_employer || "",
          education: member.education || "",
          spouseName: member.spouse_name || "",
          spousePhoneNumber: member.spouse_phone || "",
          dateOfMarriage: member.date_of_marriage || "",
          byPastor: member.by_pastor || "",
          numberOfChildren: member.number_of_children || 0,
          numberOfMales: member.number_of_male || 0,
          numberOfFemales: member.number_of_female || 0,
          nameOfParents: member.name_of_parent || "",
          nextOfKin: member.next_of_kin || "",
          yearOfConversion: member.year_of_conversion || "",
          areYouBaptized: member.are_you_baptized || "",
          baptismType: member.baptism_type || "",
          baptismDate: member.baptism_date || "",
          baptismChurch: member.baptism_church || "",
          baptismBy: member.baptism_by || "",
          nameOfPreviousChurch: member.name_of_previous_church || "",
          previousChurchAddress: member.previous_church_address || "",
          placeOfInterest: member.place_of_interest || "",
          willYouPayTithe: member.will_you_pay_tithe || "",
          communicant: member.communicant || "",
          willYouPayWelfare: member.will_you_pay_welfare || "",
          membershipDate: member.membership_date || "",
          signature: member.signature || "",
          role: member.role || "member",
          photo: member.photo || "",
          whatsappNumber: member.whatsapp_number || "",
        };
        setSelectedMember(memberData);
      }

      setIsLoading(false);
      setError(null);
    } catch (error) {
      setIsLoading(false);
      setError(error.message);
      console.error("Fetch failed:", error);
    }
  };

  useEffect(() => {
    fetchMemberData();
  }, []);

  return {
    isLoading,
    error,
    selectedMember,
    showModalOpen,
    setShowModalOpen,
    MemberDetailsModal,
  };
}
